import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCcw, Send, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton, StatusPill, fmtDate, plural } from '../../components/ui';

// Timesheets › QuickBooks: approved timesheets as QuickBooks time activities (changed ones updated
// in place, un-approved ones removed), and staff as QuickBooks employees, never twice.
// Backend: routes/qbSync.js.

const GREEN = 'bg-[#2CA01C]';
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function QuickBooksPayrollTab() {
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get('view') === 'staff' ? 'staff' : 'timesheets'));
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get('/qb-sync/status').then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false })); }, []);
  if (!status) return <div className="card"><TableSkeleton rows={4} cols={5} /></div>;
  if (!status.connected) {
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title="QuickBooks is not connected" hint="Connect QuickBooks to send timesheets to it."
          action={<Link to="/settings?tab=integrations" className="btn-primary btn-sm">Go to integrations</Link>} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="QuickBooks">
        {[['timesheets', 'Timesheets'], ['staff', 'Staff']].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${view === id ? `${GREEN} text-white` : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {view === 'timesheets' ? <TimesheetsView company={status.companyName} onStaff={() => setView('staff')} /> : <StaffView />}
    </div>
  );
}

const TS_STATUS = { ready: { tone: 'success', label: 'Ready' }, in_qb: { tone: 'info', label: 'In QuickBooks' }, not_in_qb: { tone: 'warning', label: 'Not in QuickBooks' }, refused: { tone: 'danger', label: 'Refused' } };

function TimesheetsView({ company, onStaff }) {
  const confirm = useConfirm();
  const today = new Date();
  const [from, setFrom] = useState(ymd(addDays(today, -14)));
  const [to, setTo] = useState(ymd(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.post('/qb-sync/timesheets/sync', { dry: true, from, to });
      setData(d);
      setPicked(new Set(d.workers.filter((w) => w.status === 'ready').map((w) => w.userId)));
    } catch (e) { setError(e.response?.data?.message || 'Could not prepare the timesheets.'); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const workers = data?.workers || [];
  const ready = workers.filter((w) => w.status === 'ready');
  const chosen = ready.filter((w) => picked.has(w.userId));
  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const send = async () => {
    const ok = await confirm(`Approved timesheets for ${plural(chosen.length, 'worker')}, ${fmtDate(from)} to ${fmtDate(to)}, go to ${company || 'QuickBooks'} as time activities with their start, end, break and shift details.`,
      { title: 'Send to QuickBooks?', confirmLabel: 'Send to QuickBooks' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/qb-sync/timesheets/sync', { from, to, userIds: chosen.map((w) => w.userId) });
      if (r.errors?.length) toast.error(r.errors[0] + (r.errors.length > 1 ? ` (+${r.errors.length - 1} more)` : ''), { duration: 9000 });
      if (r.synced || r.removed) toast.success(r.message);
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sending to QuickBooks failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Send timesheets to QuickBooks</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-[72ch]">
            Each approved timesheet becomes a QuickBooks time activity for the worker, with clock-on, clock-off, break and the shift details, linked to the client where they are a QuickBooks customer.
            A timesheet changed after sending is updated; one no longer approved is removed.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div><label htmlFor="qbt-from" className="text-[13px] text-slate-700 font-medium block mb-1">From</label><input id="qbt-from" type="date" className="input-field" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label htmlFor="qbt-to" className="text-[13px] text-slate-700 font-medium block mb-1">To</label><input id="qbt-to" type="date" className="input-field" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={14} aria-hidden="true" />} Show hours
          </button>
        </div>
      </div>
      <div className="card overflow-hidden">
        {loading && !data ? <TableSkeleton rows={5} cols={6} />
          : error ? <ErrorState message={error} onRetry={load} />
          : !workers.length ? <EmptyState icon={Send} title="No approved timesheets in this period" hint="Approve timesheets first, or choose other dates." />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every worker ready to send" disabled={!ready.length} checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(ready.map((w) => w.userId)))} /></th>
                      <th className="px-3 py-2.5 font-medium">Worker</th>
                      <th className="px-2 py-2.5 font-medium text-right">Shifts</th>
                      <th className="px-2 py-2.5 font-medium text-right">Hours</th>
                      <th className="px-3 py-2.5 font-medium">To send</th>
                      <th className="px-3 py-2.5 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {workers.map((w) => {
                      const st = TS_STATUS[w.status] || TS_STATUS.in_qb;
                      const todo = [w.toSend && `${w.toSend} new`, w.changed && `${w.changed} changed`, w.removing && `${w.removing} to remove`].filter(Boolean).join(', ');
                      return (
                        <tr key={w.userId} className="align-top">
                          <td className="px-4 py-3"><input type="checkbox" aria-label={`Send ${w.name}`} disabled={w.status !== 'ready'} checked={picked.has(w.userId)} onChange={() => toggle(w.userId)} /></td>
                          <td className="px-3 py-3"><p className="font-medium text-slate-900">{w.name}</p>{w.qbName && w.qbName !== w.name && <p className="text-[12px] text-slate-500">QuickBooks: {w.qbName}</p>}</td>
                          <td className="px-2 py-3 text-right tabular-nums text-slate-700">{w.shifts}</td>
                          <td className="px-2 py-3 text-right tabular-nums text-slate-700">{Number(w.hours).toFixed(2).replace(/\.00$/, '')}</td>
                          <td className="px-3 py-3 text-slate-700">{todo || <span className="text-slate-400">{w.sent ? `${w.sent} sent` : '—'}</span>}</td>
                          <td className="px-3 py-3">
                            <StatusPill tone={st.tone} label={st.label} />
                            {w.status === 'not_in_qb' && <button type="button" className="block text-[12px] text-emerald-700 font-medium mt-1 hover:underline" onClick={onStaff}>Add to QuickBooks</button>}
                            {w.errors?.map((e) => <p key={e} className="text-[12px] text-red-700 mt-1 max-w-[18rem]">{e}</p>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-600 flex-1">{fmtDate(from)} – {fmtDate(to)} · {plural(ready.length, 'worker')} ready.</p>
                <button type="button" className="btn-primary" disabled={!chosen.length || busy} onClick={send}>
                  {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />} Send {plural(chosen.length, 'worker')} to QuickBooks
                </button>
              </div>
            </>
          )}
      </div>
    </div>
  );
}

const ST_STATUS = { ready: { tone: 'success', label: 'Ready to add' }, in_qb: { tone: 'info', label: 'In QuickBooks' }, missing_name: { tone: 'warning', label: 'Name incomplete' }, contractor: { tone: 'neutral', label: 'Contractor' } };

function StaffView() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try { const { data: d } = await api.get('/qb-sync/staff'); setData(d); setPicked(new Set()); }
    catch (e) { setError(e.response?.data?.message || 'Could not read your QuickBooks employees.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const staff = data?.staff || [];
  const ready = staff.filter((s) => s.status === 'ready');
  const chosen = ready.filter((s) => picked.has(s.userId));
  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const add = async (list) => {
    if (!list.length) return;
    const ok = await confirm(`${plural(list.length, 'employee')} will be created in QuickBooks with each person's name, email, mobile, date of birth, start date, address and employee number.`,
      { title: `Add ${plural(list.length, 'staff member')} to QuickBooks?`, confirmLabel: 'Add to QuickBooks' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/qb-sync/staff/add', { userIds: list.map((s) => s.userId) });
      const bad = r.results.filter((x) => !x.ok);
      const already = r.results.filter((x) => x.existing);
      if (r.added) toast.success(`${plural(r.added, 'staff member')} added to QuickBooks`);
      if (already.length) toast.error(`Already in QuickBooks, not created again: ${already.map((x) => x.name).join(', ')}`, { duration: 9000 });
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Adding staff to QuickBooks failed'); }
    finally { setBusy(false); }
  };

  if (error) return <div className="card"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="card"><TableSkeleton rows={5} cols={4} /></div>;
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Staff in QuickBooks</h3>
          <button type="button" className="btn-primary" disabled={!ready.length || busy} onClick={() => add(ready)}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
            {ready.length ? `Add all ${plural(ready.length, 'ready staff member')} to QuickBooks` : 'Everyone ready is in QuickBooks'}
          </button>
        </div>
        <p className="text-sm text-slate-600 max-w-[72ch]">Timesheets go only to workers who are QuickBooks employees. Someone already there (same email, or same name and date of birth) is linked, never added twice.</p>
      </div>
      <div className="card overflow-hidden">
        {!staff.length ? <EmptyState icon={UserPlus} title="No active staff" hint="Staff appear here once they are in Rostery." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every staff member ready to add" disabled={!ready.length} checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(ready.map((s) => s.userId)))} /></th>
                    <th className="px-3 py-2.5 font-medium">Staff member</th><th className="px-3 py-2.5 font-medium">Date of birth</th><th className="px-3 py-2.5 font-medium">QuickBooks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((s) => {
                    const st = ST_STATUS[s.status] || ST_STATUS.in_qb;
                    return (
                      <tr key={s.userId} className="align-top">
                        <td className="px-4 py-3"><input type="checkbox" aria-label={`Add ${s.name}`} disabled={s.status !== 'ready'} checked={picked.has(s.userId)} onChange={() => toggle(s.userId)} /></td>
                        <td className="px-3 py-3"><p className="font-medium text-slate-900">{s.name}</p><p className="text-[12px] text-slate-500 break-all">{s.email}</p></td>
                        <td className="px-3 py-3 text-slate-600">{s.dob ? fmtDate(s.dob) : '—'}</td>
                        <td className="px-3 py-3">
                          <StatusPill tone={st.tone} label={st.label} />
                          {s.qbName && s.qbName !== s.name && <p className="text-[12px] text-slate-500 mt-1">As {s.qbName}</p>}
                          {s.reason && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{s.reason}</p>}
                          {s.status === 'missing_name' && <Link to={`/staff/${s.userId}/edit`} className="text-[12px] text-emerald-700 font-medium hover:underline">Fix in their profile</Link>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-600 flex-1">{plural(staff.filter((s) => s.status === 'in_qb').length, 'staff member')} in QuickBooks · {ready.length} ready to add.</p>
              <button type="button" className="btn-primary" disabled={!chosen.length || busy} onClick={() => add(chosen)}>
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />} Add {plural(chosen.length, 'staff member')} to QuickBooks
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
